import React from 'react';
import { Link } from 'react-router-dom';
import { MAX_CAMPAIGN_DRIVERS } from '../lib/campaign';

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-xl font-bold text-navy mb-3">{title}</h2>
    <div className="text-navy/80 space-y-3 leading-relaxed">{children}</div>
  </section>
);

const Terms = () => {
  return (
    <div className="bg-cream-light min-h-screen" data-testid="terms-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-3xl md:text-4xl font-bold text-navy mb-2">Terms of Service</h1>
        <p className="text-navy/60 text-sm mb-10" data-testid="terms-last-updated">Last updated: November 2, 2026</p>

        <Section title="Agreement">
          <p>By using the Advectigroup website or platform, you agree to these Terms. If you
            do not agree, do not use the service. Questions? <Link to="/contact" className="text-navy underline hover:text-gold">contact us</Link>.</p>
        </Section>

        <Section title="The service">
          <p>Advectigroup connects advertisers with drivers who carry wraps on their personal
            vehicles in the Columbus metropolitan area. We provide a dashboard for
            impression tracking, conversion attribution, and session reporting.</p>
        </Section>

        <Section title="Advertiser obligations">
          <p>You agree to (a) provide accurate company and billing information, (b) submit
            ad creative that complies with applicable laws and our content guidelines (no
            illegal goods, no hate speech, no political attack ads, no adult content),
            (c) pay invoices on schedule, and (d) not use the platform to circumvent our
            attribution or analytics.</p>
        </Section>

        <Section title="Driver obligations">
          <p>Drivers represent that they hold a valid driver&apos;s license, current vehicle
            insurance, and the right to display ad wraps on their vehicle. Drivers agree
            to operate vehicles safely and lawfully and to permit the Advecti tracking
            system to run during active sessions.</p>
        </Section>

        <Section title="Pricing & billing">
          <p>Rates depend on wrap type, campaign length, and driving versus idle time.
            Idle time is billed at 10% of the driving rate. We may adjust published rates
            for new campaigns with notice. Existing campaigns are billed at the rate in
            effect when the campaign launched.</p>
          <p data-testid="campaign-fleet-pricing-terms">Campaigns support <strong>1–{MAX_CAMPAIGN_DRIVERS} cars</strong>.
            Each additional driver after the first takes <strong>5% off the total campaign price</strong>:
            2 drivers = 5%, 3 = 10%, 4 = 15%, 5 = 20%, and 6 = 25%.
            This applies to all wrap types, including the Testimonial Partial Wrap promotion.</p>
        </Section>

        <Section title="Testimonial Promotion">
          <p data-testid="testimonial-pricing-terms">The Testimonial Partial Wrap promotion has a base rate of <strong>$681/month per driver</strong>
            before the multi-driver discount, with a fixed <strong>3-month</strong> term and up to <strong>{MAX_CAMPAIGN_DRIVERS} drivers</strong> (roughly 20% off the standard
            Partial Wrap rate) while spots remain. Eligibility requires:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>A verbal testimonial recorded halfway through the campaign.</li>
            <li>The Advectigroup name or logo included in the wrap design.</li>
          </ul>
          <p>If either condition is not met, Advecti reserves the right to charge the
            standard Partial Wrap rate ($850/mo) for the remaining campaign and any sessions
            already completed at the discounted rate.</p>
        </Section>

        <Section title="Cancellation & refunds">
          <p>Advertiser campaigns may be cancelled with 30 days&apos; written notice. Driver
            engagements may be ended by either party with reasonable notice. Refunds for
            sessions already delivered are not available, except in cases of platform
            error, which we will correct in good faith.</p>
          <p><strong>Early-termination penalty.</strong> Advertiser campaigns are billed against
            the duration the advertiser committed to at signup (e.g. a 5-month campaign).
            If the advertiser deletes their account or terminates the campaign before that
            committed end date, a two-part early-termination penalty becomes immediately due.</p>

          <p><strong>Pricing.</strong> The base monthly rate is determined by wrap type:
            Testimonial Partial Wrap <strong>$681/mo per driver</strong> (3-month term),
            Partial Wrap <strong>$850/mo</strong>, Full Wrap <strong>$1,500/mo</strong>.
            Campaigns longer than 3 months receive a volume discount of <strong>$85/mo</strong>
            per month beyond 3 for Partial Wrap, and <strong>$136/mo</strong> per month beyond 3
            for Full Wrap. (For example, a 5-month Partial Wrap campaign is billed at
            $850 − 2 × $85 = $680/mo.) Testimonial Partial Wrap campaigns are already
            promotional and receive no further campaign-length discount; the multi-driver discount still applies.</p>

          <p><strong>How the penalty is calculated.</strong> When an advertiser cancels with
            <em> M</em> months remaining of a campaign of total duration <em>D</em>:</p>
          <ol className="list-decimal pl-6 space-y-1">
            <li>
              The advertiser is billed the <strong>full base monthly rate</strong> (no volume
              discount) for any remaining months that still fall on or before month 3 of the
              campaign — that is, for <em>min(M, max(0, 3 − months elapsed))</em> months.
            </li>
            <li>
              The advertiser <strong>pays back</strong> the volume discount they had received on
              months already used — that is, <em>months elapsed × (discount unit) × (D − 3)</em>.
            </li>
            <li>
              Any remaining months that fall <strong>after month 3</strong> of the campaign are
              <strong> forgiven</strong>.
            </li>
          </ol>

          <p><strong>Worked example.</strong> A <strong>5-month Partial Wrap</strong> campaign at
            $680/mo (after the $170/mo volume discount). After 1 month, the advertiser cancels
            with 4 months remaining. Penalty:</p>
          <ul className="list-disc pl-6">
            <li>Months 2 and 3 billed at full $850/mo &nbsp;=&nbsp; <strong>$1,700</strong></li>
            <li>1 month elapsed × $170/mo volume discount clawback &nbsp;=&nbsp; <strong>$170</strong></li>
            <li>Months 4 and 5 — <strong>forgiven</strong></li>
            <li><strong>Total early-termination penalty: $1,870.</strong></li>
          </ul>

          <p>Disconnecting an individual integration (Shopify, Square, Stripe, or Manual Entry)
            — or removing the Advecti Pixel from your site — is <strong>not</strong> a campaign
            cancellation and does not trigger this penalty. The exact penalty applicable to a
            specific account is shown in the Data &amp; Privacy tab of the advertiser dashboard
            before any deletion is confirmed.</p>
        </Section>

        <Section title="Intellectual property">
          <p>Advertisers retain ownership of their creative materials. Advecti retains
            ownership of the platform, software, dashboards, and aggregated/anonymized
            performance data. You may not copy, resell, or reverse-engineer the platform.</p>
        </Section>

        <Section title="Disclaimers">
          <p>The service is provided &ldquo;as is&rdquo; without warranties of any kind. We make no
            guarantee of specific impression counts, conversion rates, revenue lift, or
            ROI. To the maximum extent permitted by law, Advecti is not liable for
            indirect, incidental, or consequential damages. Our total liability is capped
            at the fees you paid in the 12 months preceding the claim.</p>
        </Section>

        <Section title="Governing law">
          <p>These Terms are governed by the laws of the State of Ohio, USA. Any dispute
            must be brought in a court located in Franklin County, Ohio.</p>
        </Section>

        <Section title="Changes">
          <p>We may update these Terms. We will post material changes at the top of this
            page. Continued use of the service after changes constitutes acceptance.</p>
        </Section>

        <p className="text-navy/50 text-xs mt-10">
          This summary is provided in plain English for clarity. It is not a substitute for
          legal advice.
        </p>
      </div>
    </div>
  );
};

export default Terms;
